import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FolderSelect } from "@/features/documents/components/folder-select";
import type { DocumentFolder } from "@/features/documents/types";

interface MoveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: DocumentFolder[];
  /** How many documents are being moved (for the copy). */
  count: number;
  onConfirm: (folderId: string | null) => Promise<void> | void;
}

/** Pick a destination folder for one or many documents. */
export function MoveDialog({ open, onOpenChange, folders, count, onConfirm }: MoveDialogProps) {
  const [folderId, setFolderId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setFolderId(null);
  }, [open]);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm(folderId);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move {count === 1 ? "document" : `${count} documents`}</DialogTitle>
          <DialogDescription>Choose a destination folder.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="move-folder">Folder</Label>
          <FolderSelect
            id="move-folder"
            folders={folders}
            value={folderId}
            onChange={setFolderId}
            rootLabel="Client root"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={busy}>
            {busy ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
